// Dependency-free booking-offer options, wording and limits, shared by the API, the Teams tab and the forms site (they
// bundle this file, so it must not import Node modules). Contact Hub 4, spec §4.4 (rulings B1-B26 in
// docs/superpowers/plans/2026-11-03-contact-hub-04-offers-and-booking.md).
//
// Wording rules: offer messages are warm and polite, carry the first name, the study's lay title (checked by
// `studyTitleProblem`), a date/time and the private link, and never a panel number, an AN number, an answer, a staff
// note or a health word. There is no clinic address anywhere in this feature (Andrew, 03/11/2026). The approved
// answers below are seeded into cr69d_approvedanswers; the Utility templates in whatsappTemplates.ts are the same text
// with numbered variables (Meta reviews the literal text; bookingOfferOptions.test.ts holds them together).

import { CHANNEL } from './whatsappOptions';

/** cr69d_bookingoffer.cr69d_state option values (B14). Never compare states with < / >. */
export const OFFER_STATE = {
  Sent: 105600000,
  Opened: 105600001,
  Booking: 105600002,
  Booked: 105600003,
  AllGone: 105600004,
  Expired: 105600005,
  Withdrawn: 105600006,
  Ended: 105600007,
} as const;
export type OfferState = typeof OFFER_STATE[keyof typeof OFFER_STATE];

export const OFFER_STATE_LABELS: Record<OfferState, string> = {
  [OFFER_STATE.Sent]: 'Sent',
  [OFFER_STATE.Opened]: 'Opened',
  [OFFER_STATE.Booking]: 'Booking',
  [OFFER_STATE.Booked]: 'Booked',
  [OFFER_STATE.AllGone]: 'All gone',
  [OFFER_STATE.Expired]: 'Expired',
  [OFFER_STATE.Withdrawn]: 'Withdrawn',
  [OFFER_STATE.Ended]: 'Ended',
};

/** Why an offer ended (`cr69d_endreason`). `not-passed` is an ending, never an error (F5). */
export const END_REASONS = [
  'withdrawn', 'replaced', 'expired', 'no-time-left', 'all-gone', 'campaign-closed', 'not-interested', 'opted-out', 'not-passed',
] as const;
export type EndReason = typeof END_REASONS[number];

/** The endings that tell the volunteer (the `ended` message); every other ending is silent. */
export const MESSAGE_ON_END: ReadonlySet<EndReason> = new Set<EndReason>(['expired', 'no-time-left', 'all-gone', 'campaign-closed']);

/** The three messages of an offer: the offer itself, the confirmation after a booking, and the "no longer available" note. */
export const OFFER_STEPS = ['offer', 'confirmed', 'ended'] as const;
export type OfferStep = typeof OFFER_STEPS[number];

/** Approved-answer keys (`cr69d_approvedanswers`), one per step. */
export const OFFER_ANSWER_KEY: Record<OfferStep, string> = {
  offer: 'wa.offer.sent',
  confirmed: 'wa.offer.confirmed',
  ended: 'wa.offer.ended',
};
export const ALL_OFFER_ANSWER_KEYS: string[] = OFFER_STEPS.map((s) => OFFER_ANSWER_KEY[s]);

/** Keys of TEMPLATES (whatsappOptions.ts) / TEMPLATE_TEXT (whatsappTemplates.ts) for the offer messages. */
export type OfferTemplateKey = 'bookingOffer' | 'bookingConfirmed' | 'bookingOfferEnded';

const STEP_TEMPLATE: Record<OfferStep, OfferTemplateKey> = {
  offer: 'bookingOffer',
  confirmed: 'bookingConfirmed',
  ended: 'bookingOfferEnded',
};

export function offerTemplateKey(step: OfferStep): OfferTemplateKey {
  return STEP_TEMPLATE[step];
}

/**
 * The fixed payment line (B12a, agreed by Andrew 03/11/2026). The same for every study and not per-study editable: it
 * sits inside a Meta template, which cannot change without re-approval. Shown on the confirm panel, the booked page and
 * in the confirmation. It must not say "today": it is read days after the answers were given.
 */
export const BOOKING_PAYMENT_WARNING_TEXT =
  "The answers you've given us decide whether you can take part. If any of them has changed when you come in, you may not be paid for the visit.";

/** What a message-log body holds in place of the private link (the link is never stored in a log or a body). */
export const BOOKING_LINK_PLACEHOLDER = '[booking link]';

/**
 * The approved answers seeded into cr69d_approvedanswers (scripts/add-booking-offer-schema.ts). Tags: {{firstName}},
 * {{study}}, {{link}}, {{expiresOn}}, {{day}}, {{time}} only. Every text opens "Hi {{firstName}}, " so it still reads
 * well when the first name falls back to "there".
 */
export const OFFER_ANSWER_SEED: { key: string; label: string; text: string }[] = [
  {
    key: OFFER_ANSWER_KEY.offer,
    label: 'Booking offer',
    text: "Hi {{firstName}}, as you asked to take part in the {{study}} study, you can now choose the time of your first visit here: {{link}} This link works until {{expiresOn}} and is personal to you, so please don't share it.",
  },
  {
    key: OFFER_ANSWER_KEY.confirmed,
    label: 'Booking confirmed',
    text: `Hi {{firstName}}, your first visit for the {{study}} study is booked for {{day}} at {{time}}. ${BOOKING_PAYMENT_WARNING_TEXT} If you need a different time, open {{link}} and press "I need to rearrange".`,
  },
  {
    key: OFFER_ANSWER_KEY.ended,
    label: 'Booking offer ended',
    text: "Hi {{firstName}}, the visit times we sent you for the {{study}} study are no longer available, and that booking link has now closed. You don't need to do anything.",
  },
];

/** Email subjects for the three messages (the email body is the same text as the approved answer). */
export const OFFER_EMAIL_SUBJECT: Record<OfferStep, string> = {
  offer: 'Pick a time for your visit to Cutest',
  confirmed: "You're booked in at Cutest",
  ended: 'An update about your study times',
};

/** At most this many times in one offer. */
export const MAX_OFFER_SLOTS = 20;
/** A time drops off the page this many minutes before it starts; inside that hour staff book by hand. */
export const ONLINE_CUTOFF_MINUTES = 60;
/** An offer works for this many days after it is sent (B2). */
export const OFFER_TTL_DAYS = 7;
/** A Booking claim older than this is stale and may be recovered (B17). */
export const CLAIM_STALE_MS = 120000;
/**
 * The `cr69d_lasterror` on a stale Booking claim that recovery can neither complete nor release: its consent row holds
 * another bookingref of the same booking group (staff moved or re-cohorted it). Shown as "Booking needs a check"
 * (Task 12, carried from the Task 9 review); recovery then only reads it until a person fixes the row.
 */
export const CLAIM_NEEDS_CHECK_ERROR = 'confirm:needs-check';
/** Personal fields on an offer are cleared this many days after it ends (B21). */
export const PERSONAL_CLEAR_DAYS_AFTER_END = 2;
/** The offer row itself is deleted this many months after it ends (B21). */
export const OFFER_RETENTION_MONTHS = 12;
/** The booking page re-reads the times every this many seconds. */
export const PAGE_REFRESH_SECONDS = 15;
/**
 * The `followUpsLeft` entry (cr69d_followupsjson) written when an online booking could not read the study's follow-up
 * timepoints at all (the config read failed): every follow-up is left for consenting. Shown to staff as written.
 */
export const FOLLOWUPS_NOT_CHECKED = 'All follow-ups (could not be checked)';
/** Size of the offer's record of sent emails (`cr69d_emailsentjson`, F18): a claim that would not fit is refused `record-full`. */
export const OFFER_EMAIL_SENT_JSON_MAX_LENGTH = 20000;

/** The volunteer-facing strings of the booking page (owner-warm tone). Never a panel number, a name or an address. */
export const BOOKING_PAGE_TEXT = {
  notFound: "Sorry, we couldn't find these times. Please use the link in your latest message from us.",
  expired: 'This link has expired.',
  replaced: "We've sent you newer times. Please use the link in our latest message.",
  allGone: "Sorry, these times have all gone. If we can offer you new times, we'll be in touch.",
  ended: "Sorry, this offer has ended. If we can offer you new times, we'll be in touch.",
  endedOptedOut: 'This offer has ended. If you change your mind, please contact our Reception team at reception@cutest.co.uk or on 029 2062 5686.',
  justTaken: 'Sorry, that time has just been taken. Please pick another time below.',
  tooLate: 'Sorry, that time is now too soon to book online. Please pick another time below, or call us on 029 2062 5686.',
  needsStaff: "Sorry, we couldn't book this time online. Someone from our team will be in touch.",
  busy: "Just a moment, we're still booking your time...",
  error: 'Sorry, something went wrong. Please check your connection and try again.',
  rateLimited: 'Please wait a moment and try again.',
  heading: 'Pick a time for your first visit',
  firstCome: 'Times are booked first come, first served.',
  bookedHeading: "You're booked",
  rearrangeButton: 'I need to rearrange',
  rearrangeDone: "Thank you. We've let our team know, and someone will be in touch to find a new time. Your booking stays as it is until then.",
  changed: "Your booking has changed. We'll be in touch with the details.",
} as const;

/**
 * Plain words for an offer's cr69d_lasterror (`{step}:{reason}`, B19/B20), by channel and reason, for the staff panel.
 * Never includes anything from the volunteer. `confirm:*` reasons come from the booking page's Confirm; the others from
 * the message steps (`offer`, `confirmed`, `ended`). A not-passed volunteer is an ending, not an error (F5), so it has
 * no text here and would fall to the generic line if it were ever stamped.
 */
export function OFFER_ERROR_TEXT(channel: number, lastError: string): string {
  const raw = String(lastError ?? '');
  const step = raw.includes(':') ? raw.slice(0, raw.indexOf(':')) : '';
  const reason = raw.includes(':') ? raw.slice(raw.indexOf(':') + 1) : raw;
  if (step === 'confirm') {
    switch (reason) {
      case 'appointment-failed':
        return 'Booked on the board, but the calendar entry was not created. Check the calendar and add it by hand.';
      case 'failed-consent':
        return 'Their online booking was refused (they have a failed consent for this study): book them by hand on the Recruitment Board.';
      case 'already-booked':
        return 'Their online booking was refused (they are already booked for this study): book them by hand on the Recruitment Board.';
      case 'needs-check':
        return 'An online booking did not finish, and its time on the Recruitment Board has been changed since. Check the board and their booking, then contact them.';
      default:
        return `The online booking needs a check (${reason})`;
    }
  }
  switch (reason) {
    case 'not-queued':
      // The function could not queue it (a storage problem): nothing was sent.
      return step === 'offer'
        ? 'The offer could not be queued, so it was not sent; press Send message again'
        : 'The message could not be queued, so it was not sent; press Send message again';
    case 'opted-out':
      return channel === CHANNEL.Email
        ? 'They have opted out of email, so the message was not sent'
        : 'They have opted out of WhatsApp messages, so the message was not sent';
    case 'stop-received':
      return 'Their latest WhatsApp message was STOP, so the message was not sent';
    case 'no-email':
      return 'There is no email address on their record';
    case 'wrong-secret':
      return 'This link was made by the other app; send new times';
    case 'record-full':
      // The Email route's record of sent emails (cr69d_emailsentjson) is full (OFFER_EMAIL_SENT_JSON_MAX_LENGTH).
      return "This offer's record of sent emails is full, so no more emails can be sent on it; ask IT";
    case 'missing-template-params':
      // planOffer / sendOfferEmail could not build the message: most often the stored study name no longer passes
      // studyTitleProblem, or the link or a date could not be read. The offer keeps its title, so send new times.
      return 'The details could not be filled, e.g. the study name, so the message was not sent; send new times with a lay study name';
    case 'email-refused':
      return 'The mail server refused the email, so it did not reach them; check the email address on their record';
    case 'meta-failed':
      return 'WhatsApp could not deliver the message to them';
    case 'allowlist':
      return 'This volunteer is not on the test list for this app, so the message was not sent';
    case 'no-panel':
      return 'This offer has no panel number, so the message was not sent; send new times';
    case 'not-opted-in':
    case 'no-preferences':
      return 'They have not opted in to WhatsApp messages, so the message was not sent';
    case 'no-mobile':
    case 'no-destination':
      return 'There is no WhatsApp number to send to, so the message was not sent';
    case 'not-capable':
      return 'Their mobile number is not on WhatsApp, so the message was not sent';
    case 'conflicting-mobile':
      return 'Their records hold different mobile numbers, so the message was not sent; check their preferences';
    default:
      return `The message could not be sent (${reason})`;
  }
}
